import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, DataTable, Loading, formatCurrency, formatDate, BackButton, FichesDirectesPanel, niveauMessage, styleMessage, SectionRepliable } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const TYPES_DEFAUT   = ['Entreprise', 'Particulier', 'Association', 'Collectivité', 'Autre'];
const STATUTS_DEFAUT = ['Prospect', 'Client actif', 'Client inactif', 'Partenaire'];

function ModalModifier({ client, clientOptions, onSave, onClose }) {
  const [form, setForm] = useState({
    ...client,
    activites: Array.isArray(client.activites) ? client.activites : (client.activites ? JSON.parse(client.activites) : []),
    adresse_fact_differente: !!(
      client.adresse_facturation1 || client.code_postal_facturation || client.ville_facturation
    ),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activitesList, setActivitesList] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    fetch(`${API_BASE}/activites`)
      .then(r => r.json())
      .then(d => setActivitesList(Array.isArray(d) ? d.map(a => a.libelle) : []))
      .catch(() => {});
  }, []);

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block', fontSize: '0.78rem', fontWeight: 600,
    color: 'var(--text-secondary)', marginBottom: '0.25rem',
  };

  const sectionStyle = {
    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
    color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em',
  };

  function field(label, key, type = 'text') {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={labelStyle}>{label}</label>
        <input type={type} value={form[key] || ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  async function handleSave() {
    if (!form.nom_client) return setError('Le nom du client est requis');
    // Si adresse fact non cochée, on vide les champs
    const payload = { ...form };
    if (!form.adresse_fact_differente) {
      payload.adresse_facturation1 = '';
      payload.adresse_facturation2 = '';
      payload.code_postal_facturation = '';
      payload.ville_facturation = '';
    }
    setSaving(true); setError('');
    try {
      await apiFetch(`/clients/${client.client_id}`, { method: 'PUT', body: JSON.stringify(payload) });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '640px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>✏ Modifier — {client.nom_client}</h3>

        {/* INFOS GÉNÉRALES */}
        <p style={sectionStyle}>Informations générales</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Nom du client *', 'nom_client')}
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={labelStyle}>Type</label>
            <select value={form.type_client || ''} onChange={e => set('type_client', e.target.value)} style={inputStyle}>
              <option value="">— Sélectionner —</option>
              {clientOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={labelStyle}>Statut</label>
            <select value={form.statut_client || ''} onChange={e => set('statut_client', e.target.value)} style={inputStyle}>
              <option value="">— Sélectionner —</option>
              {clientOptions.statuts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        {field('SIRET', 'siret')}

        {/* REPRÉSENTANT */}
        <p style={sectionStyle}>Représentant de la société</p>
        {field('Nom du représentant', 'representant')}
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Email du représentant', 'representant_email', 'email')}
          {field('Tél. du représentant', 'representant_tel')}
        </div>

        {/* ADRESSE PRINCIPALE */}
        <p style={sectionStyle}>Adresse principale</p>
        {field('Adresse ligne 1', 'adresse1')}
        {field('Adresse ligne 2', 'adresse2')}
        <div className="grid-label" style={{ gap: '0 0.7rem' }}>
          {field('Code postal', 'code_postal')}
          {field('Ville', 'ville')}
        </div>

        {/* ADRESSE DE FACTURATION */}
        <div style={{ margin: '1rem 0 0.7rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={form.adresse_fact_differente || false}
              onChange={e => set('adresse_fact_differente', e.target.checked)}
            />
            Adresse de facturation différente
          </label>
        </div>
        {form.adresse_fact_differente && (
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '0.7rem' }}>
            {field('Adresse facturation ligne 1', 'adresse_facturation1')}
            {field('Adresse facturation ligne 2', 'adresse_facturation2')}
            <div className="grid-label" style={{ gap: '0 0.7rem' }}>
              {field('Code postal', 'code_postal_facturation')}
              {field('Ville', 'ville_facturation')}
            </div>
          </div>
        )}

        {/* CONTACT PRINCIPAL */}
        <p style={sectionStyle}>Contact principal</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Prénom', 'contact_prenom')}
          {field('Nom', 'contact_nom')}
          {field('Email', 'contact_email', 'email')}
          {field('Tél. portable', 'contact_tel_portable')}
          {field('Tél. fixe', 'tel_fixe')}
          {field('Portable société', 'tel_portable')}
        </div>
        {field('Fonction / Profession', 'contact_profession')}

        {/* CONTACT SECONDAIRE */}
        <p style={sectionStyle}>Contact secondaire</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Prénom', 'contact2_prenom')}
          {field('Nom', 'contact2_nom')}
          {field('Email', 'contact2_email', 'email')}
          {field('Tél. portable', 'contact2_tel_portable')}
          {field('Tél. fixe', 'contact2_tel_fixe')}
        </div>
        {field('Fonction / Profession', 'contact2_profession')}

        {/* ACTIVITÉS */}
        {activitesList.length > 0 && (
          <>
            <p style={sectionStyle}>Activité(s) de l'entreprise</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', marginBottom: '0.8rem' }}>
              {activitesList.map(a => {
                const checked = (form.activites || []).includes(a);
                return (
                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => set('activites', checked ? form.activites.filter(x => x !== a) : [...(form.activites || []), a])}
                    />
                    {a}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {/* CONDITIONS COMMERCIALES */}
        <p style={sectionStyle}>Conditions commerciales</p>
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={labelStyle}>Code client SAGE</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="text"
              value={form.code_client_sage || ''}
              onChange={e => set('code_client_sage', e.target.value)}
              placeholder="ex : CLI00123"
              style={{ ...inputStyle, width: '160px' }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Utilisé pour l'export Sage (préfacturation)
            </span>
          </div>
        </div>
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={labelStyle}>Remise client (%)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={form.taux_remise || 0}
              onChange={e => set('taux_remise', e.target.value)}
              style={{ ...inputStyle, width: '120px' }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {form.taux_remise > 0 ? `→ ${form.taux_remise}% de réduction appliquée automatiquement sur les devis` : 'Aucune remise par défaut'}
            </span>
          </div>
        </div>

        {/* AUTRES */}
        <p style={sectionStyle}>Autres</p>
        {field('Site internet', 'site_internet', 'url')}
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '0.7rem' }}>
          <input type="checkbox" checked={form.actif ?? true} onChange={e => set('actif', e.target.checked)} />
          Client actif
        </label>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, token } = useAuth();
  const { data: client, loading, reload } = useApi(`/clients/${id}`);
  const { data: clientOptionsData } = useApi('/client-options');
  const clientOptions = {
    types:   clientOptionsData?.types   || TYPES_DEFAUT,
    statuts: clientOptionsData?.statuts || STATUTS_DEFAUT,
  };
  const [adjClients, setAdjClients] = useState({ prev: null, next: null });
  const [modalModif, setModalModif] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fiche d'inscription directe
  const [fichesRefresh, setFichesRefresh] = useState(0); // incrémenté pour recharger la liste des fiches directes
  const [inscLien, setInscLien] = useState(null);
  const [inscEmail, setInscEmail] = useState('');
  const [inscFormationId, setInscFormationId] = useState('');
  const [inscFormationLabel, setInscFormationLabel] = useState('');
  const [inscSessionId, setInscSessionId] = useState('');
  const [inscNotes, setInscNotes] = useState('');
  const [inscEmailBody, setInscEmailBody] = useState('');
  const [inscShowModal, setInscShowModal] = useState(false);
  const [inscSending, setInscSending] = useState(false);
  const [inscMsg, setInscMsg] = useState('');
  const [inscFormations, setInscFormations] = useState([]);
  const [inscSessions, setInscSessions] = useState([]);

  useEffect(() => {
    if (!id) return;
    apiFetch(`/clients/${id}/adjacent`).then(d => setAdjClients(d)).catch(() => {});
  }, [id]);

  // Charger les formations dès l'ouverture du modal
  useEffect(() => {
    if (!inscShowModal) return;
    fetch(`${API_BASE}/formations?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setInscFormations(d.data || []))
      .catch(() => {});
  }, [inscShowModal, token]);

  // Charger les sessions quand une formation est sélectionnée
  useEffect(() => {
    if (!inscFormationId) { setInscSessions([]); setInscSessionId(''); return; }
    fetch(`${API_BASE}/sessions?formation_id=${inscFormationId}&limit=100`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setInscSessions(d.data || []))
      .catch(() => {});
  }, [inscFormationId, token]);

  // Recalculer le message par défaut quand formation ou session change
  useEffect(() => {
    if (inscLien) return; // Lien déjà créé, ne pas écraser
    const session = inscSessions.find(s => s.session_id === inscSessionId);
    const dateInfo = session
      ? ` — session du ${session.date_debut ? new Date(session.date_debut).toLocaleDateString('fr-FR') : ''}${session.date_fin && session.date_fin !== session.date_debut ? ' au ' + new Date(session.date_fin).toLocaleDateString('fr-FR') : ''}${session.lieu ? ', ' + session.lieu : ''}`
      : '';
    const formationTxt = inscFormationLabel || '';
    setInscEmailBody(
`Bonjour,

Veuillez trouver ci-dessous le lien pour compléter votre fiche d'inscription${formationTxt ? ` pour la formation "${formationTxt}"` : ''}${dateInfo}.

Merci de renseigner toutes les informations demandées et de valider votre inscription via le lien ci-dessous.

Cordialement`
    );
  }, [inscFormationLabel, inscSessionId, inscSessions, inscLien]);

  async function genererLienDirecte() {
    setInscSending(true); setInscMsg('');
    try {
      const r = await fetch(`${API_BASE}/inscription-liens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          client_id: id,
          intitule_formation: inscFormationLabel,
          session_id: inscSessionId || null,
          notes_client: inscNotes,
          email_client: inscEmail,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setInscMsg('Erreur : ' + (d.error || 'inconnue')); setInscSending(false); return null; }
      setInscLien(d);
      setFichesRefresh(k => k + 1);
      setInscMsg('Lien créé avec succès.');
      setInscSending(false);
      return d;
    } catch (e) { setInscMsg('Erreur réseau'); setInscSending(false); return null; }
  }

  async function genererEtEnvoyer() {
    const lienCree = await genererLienDirecte();
    if (!lienCree) return;
    await envoyerEmailDirecte(lienCree);
  }

  async function envoyerEmailDirecte(lienObj) {
    const emailDest = inscEmail || lienObj.email_client;
    if (!emailDest) { setInscMsg('Veuillez saisir un email.'); return; }
    setInscSending(true); setInscMsg('');
    try {
      const r = await fetch(`${API_BASE}/inscription-liens/${lienObj.id}/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailDest, lien: lienObj.lien, message_personnalise: inscEmailBody }),
      });
      const d = await r.json();
      if (r.ok) { setInscMsg('Email envoyé avec succès.'); setFichesRefresh(k => k + 1); }
      else setInscMsg('Erreur : ' + (d.error || 'inconnue'));
    } catch (e) { setInscMsg('Erreur réseau'); }
    finally { setInscSending(false); }
  }

  function ouvrirModalInscription(lienExistant = null) {
    setInscLien(lienExistant);
    setInscEmail(client.contact_email || '');
    setInscFormationId('');
    setInscFormationLabel('');
    setInscSessionId('');
    setInscNotes('');
    setInscMsg('');
    setInscShowModal(true);
  }

  if (loading || !client) return <Loading />;

  async function handleArchiver() {
    if (!window.confirm(`Archiver le client "${client.nom_client}" ?\nIl sera masqué des listes mais pourra être restauré.`)) return;
    setDeleting(true);
    try {
      await apiFetch(`/clients/${id}`, { method: 'DELETE' });
      navigate('/clients');
    } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
  }

  async function handleRestaurer() {
    setDeleting(true);
    try {
      await apiFetch(`/clients/${id}/restaurer`, { method: 'POST' });
      window.location.reload();
    } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
  }

  const aAdresseFact = !!(client.adresse_facturation1 || client.code_postal_facturation || client.ville_facturation);

  const devisColumns = [
    { header: 'N° Devis', key: 'devis_id' },
    { header: 'Formation', render: r => r.intitule_formation?.substring(0, 50) + (r.intitule_formation?.length > 50 ? '...' : '') },
    { header: 'Qté', key: 'qte' },
    { header: 'PU HT', render: r => formatCurrency(r.pu_ht) },
    { header: 'Total HT', render: r => formatCurrency(r.total_ht) },
    { header: 'Date', render: r => formatDate(r.start_date) },
    { header: 'Session liée', render: r => {
      const sessions = r.sessions_liees || [];
      if (!sessions.length) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
      return (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
          {sessions.map(sid => (
            <span key={sid}
              onClick={e => { e.stopPropagation(); navigate(`/sessions/${sid}`); }}
              style={{ color: 'var(--accent-blue)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, fontSize: '0.85rem' }}>
              {sid}
            </span>
          ))}
        </span>
      );
    }},
    { header: 'Statut', render: r => <Badge>{r.statut_devis}</Badge> },
  ];

  // Sessions du client (ses groupes dans les sessions)
  const statutSession = r => {
    if (r.annulee) return <Badge variant="badge-danger">Annulée</Badge>;
    if (r.reportee) return <Badge variant="badge-warning">Reportée</Badge>;
    const today = new Date().toISOString().slice(0, 10);
    const debut = (r.date_debut || '').slice(0, 10);
    const fin = (r.date_fin || '').slice(0, 10);
    if (debut > today) return <Badge variant="badge-info">À venir</Badge>;
    if (fin >= today && !r.terminee) return <Badge variant="badge-warning">En cours</Badge>;
    return <Badge variant="badge-success">Terminée</Badge>;
  };
  const sessionsColumns = [
    { header: 'Session', render: r => <strong>{r.sous_session_id || r.session_id}</strong> },
    { header: 'Formation', render: r => r.intitule_formation?.substring(0, 50) + (r.intitule_formation?.length > 50 ? '...' : '') },
    { header: 'Date(s)', render: r => r.dates_texte || formatDate(r.date_debut) },
    { header: 'Lieu', render: r => r.lieu ? r.lieu.substring(0, 40) + (r.lieu.length > 40 ? '...' : '') : '—' },
    { header: 'Stagiaires', render: r => r.nb_stagiaires || 0 },
    { header: 'Statut', render: statutSession },
  ];

  return (
    <div className="page">
      <PageHeader
        title={client.nom_client}
        subtitle={`${client.client_id} — ${client.ville || ''}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate(`/clients/${adjClients.prev}`)}
              disabled={!adjClients.prev} style={{ opacity: adjClients.prev ? 1 : 0.35 }}
              title={adjClients.prev || 'Premier client'}>◀ Précédent</button>
            <button className="btn-secondary" onClick={() => navigate(`/clients/${adjClients.next}`)}
              disabled={!adjClients.next} style={{ opacity: adjClients.next ? 1 : 0.35 }}
              title={adjClients.next || 'Dernier client'}>Suivant ▶</button>
            {hasRole('utilisateur') && (
              <>
                {client.actif === false && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉ</span>
                )}
                <button className="btn-secondary" onClick={() => setModalModif(true)}>✏ Modifier</button>
                {client.actif !== false ? (
                  <button onClick={handleArchiver} disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#f87171', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '🗑 Archiver'}
                  </button>
                ) : (
                  <button onClick={handleRestaurer} disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--accent-emerald)', background: 'transparent', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '↩ Restaurer'}
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="detail-grid">
        {/* INFOS GÉNÉRALES */}
        <div className="card">
          <h3 className="card-title">Informations</h3>
          <div className="info-grid">
            <div className="info-row"><label>Type</label><Badge>{client.type_client}</Badge></div>
            <div className="info-row"><label>Statut</label><Badge>{client.statut_client}</Badge></div>
            <div className="info-row"><label>SIRET</label><span>{client.siret || '—'}</span></div>
            <div className="info-row"><label>Tél. fixe</label><span>{client.tel_fixe || '—'}</span></div>
            <div className="info-row"><label>Tél. portable</label><span>{client.tel_portable || '—'}</span></div>
            <div className="info-row"><label>Site web</label><span>{client.site_internet || '—'}</span></div>
            {client.code_client_sage && (
              <div className="info-row">
                <label>Code SAGE</label>
                <span style={{ fontWeight: 700, color: 'var(--accent-blue)', fontFamily: 'monospace' }}>{client.code_client_sage}</span>
              </div>
            )}
            {parseFloat(client.taux_remise) > 0 && (
              <div className="info-row">
                <label>Remise</label>
                <span style={{ fontWeight: 700, color: '#16a34a' }}>{client.taux_remise}%</span>
              </div>
            )}
          </div>

          {/* ACTIVITÉS */}
          {(() => {
            const acts = Array.isArray(client.activites) ? client.activites : (client.activites ? JSON.parse(client.activites) : []);
            if (!acts.length) return null;
            return (
              <>
                <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Activité(s)</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem 0.5rem' }}>
                  {acts.map(a => <Badge key={a}>{a}</Badge>)}
                </div>
              </>
            );
          })()}

          {/* REPRÉSENTANT */}
          <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Représentant</h3>
          <div className="info-grid">
            <div className="info-row"><label>Nom</label><span>{client.representant || '—'}</span></div>
            <div className="info-row"><label>Email</label><span>{client.representant_email || '—'}</span></div>
            <div className="info-row"><label>Tél.</label><span>{client.representant_tel || '—'}</span></div>
          </div>
        </div>

        {/* ADRESSES + CONTACT */}
        <div className="card">
          <h3 className="card-title">Adresse principale</h3>
          <div className="info-grid">
            <div className="info-row"><label>Adresse</label>
              <span>{client.adresse1}{client.adresse2 ? <><br />{client.adresse2}</> : ''}</span>
            </div>
            <div className="info-row"><label>CP / Ville</label><span>{client.code_postal} {client.ville}</span></div>
          </div>

          {aAdresseFact && (
            <>
              <h3 className="card-title" style={{ marginTop: '1.5rem' }}>
                Adresse de facturation
              </h3>
              <div className="info-grid">
                <div className="info-row"><label>Adresse</label>
                  <span>{client.adresse_facturation1}{client.adresse_facturation2 ? <><br />{client.adresse_facturation2}</> : ''}</span>
                </div>
                <div className="info-row"><label>CP / Ville</label>
                  <span>{client.code_postal_facturation} {client.ville_facturation}</span>
                </div>
              </div>
            </>
          )}

          <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Contact principal</h3>
          <div className="info-grid">
            <div className="info-row"><label>Nom</label><span>{client.contact_prenom} {client.contact_nom}</span></div>
            <div className="info-row"><label>Email <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>(envoi des documents)</span></label><span>{client.contact_email || '—'}</span></div>
            <div className="info-row"><label>Tél.</label><span>{client.contact_tel_portable || client.contact_tel_fixe || '—'}</span></div>
            <div className="info-row"><label>Fonction</label><span>{client.contact_profession || '—'}</span></div>
          </div>

          {(client.contact2_nom || client.contact2_prenom || client.contact2_email || client.contact2_tel_portable || client.contact2_tel_fixe || client.contact2_profession) && (
            <>
              <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Contact secondaire</h3>
              <div className="info-grid">
                <div className="info-row"><label>Nom</label><span>{[client.contact2_prenom, client.contact2_nom].filter(Boolean).join(' ') || '—'}</span></div>
                <div className="info-row"><label>Email <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>(envoi des documents)</span></label><span>{client.contact2_email || '—'}</span></div>
                <div className="info-row"><label>Tél.</label><span>{client.contact2_tel_portable || client.contact2_tel_fixe || '—'}</span></div>
                <div className="info-row"><label>Fonction</label><span>{client.contact2_profession || '—'}</span></div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* NOTES */}
      {client.notes && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">Notes</h3>
          <p className="notes-text" style={{ whiteSpace: 'pre-wrap' }}>{client.notes}</p>
        </div>
      )}

      {/* SESSIONS — menu repliable (clic sur l'en-tête : afficher / masquer) */}
      <div style={{ marginTop: '1rem' }}>
        <SectionRepliable title={`Sessions (${client.sessions?.length || 0})`}>
          <div style={{ padding: '0.75rem' }}>
            {client.sessions?.length
              ? <DataTable columns={sessionsColumns} data={client.sessions} onRowClick={r => navigate(`/sessions/${r.session_id}`)} />
              : <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', margin: 0 }}>Aucune session pour ce client.</p>}
          </div>
        </SectionRepliable>

        {/* DEVIS — même principe */}
        <SectionRepliable title={`Devis (${client.devis?.length || 0})`}
          actions={<button className="btn-primary" onClick={() => navigate(`/devis/nouveau?client=${id}`)}>+ Nouveau devis</button>}>
          <div style={{ padding: '0.75rem' }}>
            <DataTable
              columns={devisColumns}
              data={client.devis}
              onRowClick={r => navigate(`/devis/${r.devis_id}`)}
            />
          </div>
        </SectionRepliable>
      </div>

      {/* FICHES D'INSCRIPTION DIRECTES — les liens rattachés à une session se valident dans la session ;
          ici : liens sans session (actions complètes) et accès à la session pour les autres */}
      {hasRole('utilisateur') && (
        <FichesDirectesPanel
          clientId={id}
          liensSessionEnLecture
          refreshKey={fichesRefresh}
          onRenvoyer={ouvrirModalInscription}
          onNouvelle={() => ouvrirModalInscription(null)}
          messageVide="Aucun lien d'inscription directe envoyé pour ce client."
        />
      )}

      {/* Modal fiche d'inscription directe */}
      {inscShowModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => { if (e.target === e.currentTarget) setInscShowModal(false); }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '1.8rem', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#7c3aed' }}>
              {inscLien ? 'Renvoyer la fiche d\'inscription' : 'Nouvelle fiche d\'inscription directe'}
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 1.4rem' }}>
              Client : <strong>{client.nom_client}</strong>
            </p>

            {/* ─ Formation ─ */}
            {!inscLien && (
              <div style={{ marginBottom: '0.8rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                  Formation <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select value={inscFormationId}
                  onChange={e => {
                    const opt = e.target.options[e.target.selectedIndex];
                    setInscFormationId(e.target.value);
                    setInscFormationLabel(opt.dataset.label || '');
                    setInscSessionId('');
                  }}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }}>
                  <option value="">— Choisir une formation —</option>
                  {inscFormations.map(f => (
                    <option key={f.formation_id} value={f.formation_id} data-label={f.intitule}>
                      {f.intitule}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ─ Session (optionnelle) ─ */}
            {!inscLien && (
              <div style={{ marginBottom: '0.8rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                  Session associée <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span>
                </label>
                <select value={inscSessionId} onChange={e => setInscSessionId(e.target.value)}
                  disabled={!inscFormationId || inscSessions.length === 0}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: !inscFormationId ? '#94a3b8' : 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }}>
                  <option value="">— Aucune session sélectionnée —</option>
                  {inscSessions.map(s => {
                    const dd = s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '';
                    const df = s.date_fin   ? new Date(s.date_fin).toLocaleDateString('fr-FR')   : '';
                    const dates = dd === df ? dd : dd && df ? `${dd} – ${df}` : dd || df;
                    return (
                      <option key={s.session_id} value={s.session_id}>
                        {s.session_id} — {dates}{s.lieu ? ' · ' + s.lieu : ''}
                      </option>
                    );
                  })}
                </select>
                {inscFormationId && inscSessions.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0' }}>Aucune session pour cette formation.</p>
                )}
              </div>
            )}

            {/* ─ Email ─ */}
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                Email du destinataire <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input value={inscEmail} onChange={e => setInscEmail(e.target.value)}
                placeholder="email@client.fr"
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' }} />
            </div>

            {/* ─ Lien existant ─ */}
            {inscLien && (
              <div style={{ marginBottom: '0.8rem', padding: '0.6rem 0.9rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: '0.8rem', wordBreak: 'break-all', color: '#6b7280' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: 'var(--text-primary)' }}>{inscLien.intitule_formation}</div>
                {inscLien.lien}
              </div>
            )}

            {/* ─ Message email ─ */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.3rem' }}>
                Message envoyé au client
                <span style={{ marginLeft: '0.4rem', fontWeight: 400, color: '#94a3b8' }}>(modifiable)</span>
              </label>
              <textarea value={inscEmailBody} onChange={e => setInscEmailBody(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
              <p style={{ fontSize: '0.74rem', color: '#94a3b8', margin: '0.2rem 0 0' }}>
                Le bouton "Compléter ma fiche d'inscription" et la signature sont ajoutés automatiquement.
              </p>
            </div>

            {inscMsg && (
              <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.8rem', borderRadius: '6px',
                ...styleMessage(niveauMessage(inscMsg)), fontSize: '0.85rem' }}>
                {inscMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => setInscShowModal(false)}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem' }}>
                Annuler
              </button>
              {inscLien ? (
                <>
                  <button onClick={() => { navigator.clipboard?.writeText(inscLien.lien); setInscMsg('Lien copié !'); }}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>
                    📋 Copier le lien
                  </button>
                  <button onClick={() => envoyerEmailDirecte(inscLien)} disabled={inscSending || !inscEmail}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: (!inscSending && inscEmail) ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.88rem', opacity: (!inscSending && inscEmail) ? 1 : 0.6 }}>
                    {inscSending ? 'Envoi…' : '✉ Envoyer par email'}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={genererLienDirecte}
                    disabled={inscSending || !inscFormationId}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #8b5cf6', background: 'transparent', color: '#8b5cf6', cursor: (inscSending || !inscFormationId) ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: (inscSending || !inscFormationId) ? 0.5 : 1 }}>
                    {inscSending ? '…' : 'Créer le lien seul'}
                  </button>
                  <button onClick={genererEtEnvoyer}
                    disabled={inscSending || !inscEmail || !inscFormationId}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: (inscSending || !inscEmail || !inscFormationId) ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: (inscSending || !inscEmail || !inscFormationId) ? 0.5 : 1 }}>
                    {inscSending ? 'En cours…' : '✉ Créer et envoyer par email'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {modalModif && (
        <ModalModifier
          client={client}
          clientOptions={clientOptions}
          onSave={() => { setModalModif(false); reload(); }}
          onClose={() => setModalModif(false)}
        />
      )}
    </div>
  );
}
